import { memo, useCallback, useState } from 'react'
import { announcementsApi, type Announcement, type MemberSummary } from '../../lib/infoserviceApi.js'
import { dayRangeKeys, isDayKey, isDayRange } from '../../lib/announcementGrid.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

interface NewAnnouncementProps {
  channelId: string
  members: MemberSummary[]
  today: string
  onCreated: (announcement: Announcement) => void
  onClose: () => void
}

/**
 * Création d'une annonce : adhérent, identité, période de diffusion, image
 * facultative. Sans adhérent choisi, celui de l'organisme est retrouvé ou créé
 * par le serveur : il apparaît ensuite dans l'onglet Adhérents.
 * L'image déposée ici est nommée d'après le premier jour coché, d'où l'ordre :
 * l'annonce et ses jours d'abord, l'image ensuite.
 */
export const NewAnnouncementModal = memo(function NewAnnouncementModal({ channelId, members, today, onCreated, onClose }: NewAnnouncementProps) {
  const [organizationLabel, setOrganization] = useState('')
  const [eventLabel, setEvent] = useState('')
  const [memberId, setMemberId] = useState('')
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const pickMember = useCallback((id: string) => {
    setMemberId(id)
    const member = members.find(entry => entry.id === id)
    if (member && !organizationLabel.trim()) setOrganization(member.name)
  }, [members, organizationLabel])

  const submit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault()
    if (!organizationLabel.trim()) {
      setError('L’organisme est obligatoire.')
      return
    }
    if ((from || to) && !isDayRange(from, to)) {
      setError(from && to && isDayKey(from) && isDayKey(to)
        ? 'La période doit finir après son début.'
        : 'La période de diffusion est incomplète.')
      return
    }
    setBusy(true)
    setError('')
    try {
      let created = await announcementsApi.create({
        channelId,
        organizationLabel: organizationLabel.trim(),
        eventLabel: eventLabel.trim(),
        memberId: memberId || null,
        days: from && to ? dayRangeKeys(from, to) : [],
      })
      if (file) created = await announcementsApi.uploadImage(created.id, file)
      onCreated(created)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Création impossible')
      setBusy(false)
    }
  }, [channelId, eventLabel, file, from, memberId, onCreated, organizationLabel, to])

  return (
    <ModalShell title="Nouvelle annonce" onClose={onClose} closeDisabled={busy}>
      <form className="tp-modal-form tp-modal__body new-announcement" onSubmit={event => void submit(event)}>
        <label className="tp-form-field">
          <span className="tp-field-label">Adhérent</span>
          <select className="tp-input" value={memberId} onChange={event => pickMember(event.target.value)}>
            <option value="">D’après le nom de l’organisme</option>
            {members.map(member => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
        </label>
        <div className="tp-form-grid">
          <label className="tp-form-field">
            <span className="tp-field-label">Organisme</span>
            <input className="tp-input" required autoFocus value={organizationLabel} placeholder="MAIRIE CHAMPAGNE" onChange={event => setOrganization(event.target.value)} />
          </label>
          <label className="tp-form-field">
            <span className="tp-field-label">Événement</span>
            <input className="tp-input" value={eventLabel} placeholder="ciné les gendarmes" onChange={event => setEvent(event.target.value)} />
          </label>
        </div>
        <div className="tp-form-grid">
          <label className="tp-form-field">
            <span className="tp-field-label">Diffusion du</span>
            <input className="tp-input" type="date" value={from} onChange={event => setFrom(event.target.value)} />
          </label>
          <label className="tp-form-field">
            <span className="tp-field-label">au</span>
            <input className="tp-input" type="date" value={to} onChange={event => setTo(event.target.value)} />
          </label>
        </div>
        <label className="tp-form-field">
          <span className="tp-field-label">Image (PNG ou JPEG, 1920×1080)</span>
          <input className="tp-input" type="file" accept="image/png,image/jpeg" onChange={event => setFile(event.target.files?.[0] ?? null)} />
          <span className="announcement-drawer__hint">
            Déposée dans INFOSERVICES/ANNONCES sous un nom daté du premier jour de diffusion. Vous pourrez aussi la choisir plus tard dans la fiche.
          </span>
        </label>
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
        <div className="tp-modal-actions">
          <button type="button" className="tp-button" onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className="tp-button tp-button--primary" disabled={busy}>{busy && <Spinner />} Créer l’annonce</button>
        </div>
      </form>
    </ModalShell>
  )
})
